import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories, heroProductImages, MAIN_SITE_URL } from "@/mocks/categories";

const mockProducts = [
  { id: 1, name: "Air Runner 2026", catKey: "shoes", price: "Varies" },
  { id: 2, name: "Everyday Hoodie", catKey: "hoodies", price: "Varies" },
  { id: 3, name: "Classic Tee", catKey: "tshirts", price: "Varies" },
  { id: 4, name: "City Jacket", catKey: "jackets", price: "Varies" },
  { id: 5, name: "Crossbody Bag", catKey: "bags", price: "Varies" },
];

export default function Hero() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);

  const filtered = mockProducts.filter((p) => {
    const matchesQuery = p.name.toLowerCase().includes(query.toLowerCase());
    const matchesCat = activeCat ? p.catKey === activeCat : true;
    return matchesQuery && matchesCat;
  });

  return (
    <section className="relative overflow-hidden bg-background-50">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,oklch(var(--primary-100)/0.5),transparent_55%)]" />
      <div className="relative mx-auto grid w-full grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-20">
        <div className="w-full">
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-sparkling-line"></i>
            </span>
            {t("hero.badge")}
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl lg:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            {t("hero.subtitle")}
          </p>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-500">
            {t("hero.supporting")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("hero.cta.primary")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
            >
              {t("hero.cta.secondary")}
            </a>
            <Link
              to="/usfans-spreadsheet-guide"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
            >
              {t("hero.cta.guide")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>

          <p className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
            <span className="w-4 h-4 flex items-center justify-center text-primary-600">
              <i className="ri-shield-check-line"></i>
            </span>
            {t("hero.trust")}
          </p>
        </div>

        {/* Spreadsheet dashboard mockup */}
        <div className="w-full">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-table-line"></i>
                </span>
                <span className="font-heading text-sm font-semibold text-foreground-950">
                  {t("brand.name")}
                </span>
              </div>
              <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                Spreadsheet
              </span>
            </div>

            <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-3 py-2">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products…"
                className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {["shoes", "hoodies", "tshirts", "jackets", "bags"].map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveCat(activeCat === key ? null : key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors cursor-pointer ${
                    activeCat === key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {t(`cat.${key}`)}
                </button>
              ))}
            </div>

            <div className="mt-4 overflow-hidden rounded-md border border-background-200">
              <div className="grid grid-cols-[1fr_auto_auto] gap-2 border-b border-background-200 bg-background-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                <span>Product</span>
                <span>Category</span>
                <span>Price</span>
              </div>
              {filtered.length > 0 ? (
                filtered.map((p) => {
                  const img = heroProductImages[p.catKey] || categories.find((c) => c.key === p.catKey)?.image;
                  return (
                    <div
                      key={p.id}
                      className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b border-background-100 px-3 py-2 last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={img}
                          alt={p.name}
                          className="h-8 w-8 rounded object-cover"
                        />
                        <span className="truncate text-sm text-foreground-800">{p.name}</span>
                      </div>
                      <span className="whitespace-nowrap text-xs text-foreground-500">
                        {t(`cat.${p.catKey}`)}
                      </span>
                      <span className="whitespace-nowrap text-xs font-medium text-foreground-600">
                        {p.price}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-6 text-center text-sm text-foreground-400">
                  No products found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}